import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, Link2, Send, ArrowRight, History, Hash, GitBranch } from 'lucide-react';
import { ConfirmModal } from './ui/Modal';
import ShareTaskToChatModal from './ShareTaskToChatModal';
import UserAvatar from './ui/UserAvatar';
import { labelIdsOf, paletteOf } from '../extensions/bundled/task-manager/boardLabels';
import LexicalRichEditor from './ui/LexicalRichEditorLazy';
import OsScroll from './ui/OsScroll';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { useBoardsUiStore } from '../extensions/bundled/task-manager/boardsUiStore';
import { buildComment } from '../extensions/bundled/task-manager/commentMentions';
import { commentNeedsHtml } from '../extensions/bundled/task-manager/commentRichText';
import CommentComposer from './boards/CommentComposer';
import TaskCommentList from './boards/TaskCommentList';
import TaskModalHeader from './boards/TaskModalHeader';
import TaskMetaChips from './boards/TaskMetaChips';
import TaskSubtasks from './boards/TaskSubtasks';
import TaskCommits from './boards/TaskCommits';
import { taskKeyMenuItems, useTaskAliasKeys, useTaskKey } from './boards/TaskKeys';
import { assigneeSelectValue } from '../extensions/bundled/task-manager/boardFilters';
import useScopeItemHistory from './git/useScopeItemHistory';
import { personalScopeId } from '../services/personalScope';
import { taskLink } from '../utils/boardLinks';
import { copyTextToClipboard } from '../utils/clipboard';
import useTaskDescriptionCollab from '../hooks/useTaskDescriptionCollab';
import AiContentChip from './ui/AiContentChip';
import { aiMarkState } from '../services/ai/aiAuthorship';
import './TaskDetailModal.css';
import './boards/TaskModal.css';


const UNASSIGNED = '';

// Members are fetched once per board mount by TaskBoardTab and cached in
// boardsUiStore; this modal only reads that cache, so opening a card never
// issues a request of its own.
const EMPTY_MEMBERS = [];


const memberLabel = (m) => m.name || m.email || 'Unknown member';

function newId() {
  return (crypto.randomUUID && crypto.randomUUID()) || `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Compact relative timestamp ("just now", "5m", "3h", "2d") with a full-date fallback. */
function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const sec = Math.round(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Title + rich-text description + comment thread for one Task Manager card.
 *
 * `teamId` is null for a private-board task (no presence/collab overhead at
 * all — the description editor gets the same plain HTML value/onChange it
 * always has) and the board's team id for a team-board task, which turns on:
 *   - task-level presence ({task_id}) for as long as this modal is mounted
 *   - per-line ("block") live collab on the description via LexicalRichEditor's
 *     valueBlocks/onBlocksChange/collab props (see the description section
 *     below for the echo-safety and reconcile wiring).
 * `isActive` mirrors TaskBoardTab's own prop: this component is kept
 * mounted-but-hidden when the user navigates to a different extension tab
 * (see TaskBoardTab), so presence must clear on that transition too, not
 * just on modal close.
 */
export default function TaskDetailModal({
  task,
  saveState = 'saved',
  teamId = null,
  // Needed only to build a share pointer — the card is {task_id, board_id,
  // team_id} and the board id is not derivable from the task alone.
  boardId = null,
  // The board's label palette. Passed in rather than looked up: the modal has
  // no board, and labels are stored as ids that only the palette can resolve.
  boardLabels = null,
  // The board's lists ({ id, name }) and how to move the task to one ("Move
  // to" in the header). Absent (the chat quick view) → no Move to.
  lists = null,
  onMove = null,
  // A new label typed in the Labels dropdown: added to the board and ticked for this task.
  onAddLabel = null,
  isActive = true,
  onClose,
  onChange,
  onDelete,
}) {
  const [title, setTitle] = useState(task.title || '');
  // The task's key on the open board (TaskKeysContext); null in the chat quick view.
  const taskKey = useTaskKey(task.id);
  const aliasKeys = useTaskAliasKeys(task.id);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const user = useAuthStore((s) => s.user);
  const myName = user?.name || user?.email || 'You';
  const currentUserId = user?.id;
  const isTeamBoard = teamId != null;

  // Team roster for the assignee picker and the "@" mention picker.
  const teamMembersByTeam = useBoardsUiStore((s) => s.teamMembersByTeam);
  const members = (isTeamBoard && teamMembersByTeam[String(teamId)]) || EMPTY_MEMBERS;
  // Option building, the "Me" row, the avatars and the keep-an-unknown-id-
  // selectable rule all live in MemberSelect now, so the card avatar and this
  // modal cannot drift apart. `assigneeSelectValue` stays the shared answer to
  // "what value does the picker show" and is still asserted in the tests.
  const assigneeValue = assigneeSelectValue(task, UNASSIGNED);
  const palette = useMemo(() => paletteOf({ labels: boardLabels }), [boardLabels]);
  const heldLabels = useMemo(() => labelIdsOf(task), [task]);

  // Inline images in the description — the same DocMediaPlugin the documents
  // editor mounts. It is opt-in (LexicalRichEditor's `media` defaults to null)
  // and tasks were simply never wired for it; nothing about a task made it
  // unavailable.
  //
  // `docId` is the task id. The upload endpoint authorises by SCOPE — the
  // session's own user id for personal, team membership plus an edit-capable
  // role for team — and treats doc_uid purely as a storage path segment,
  // validated against [A-Za-z0-9_-]{1,64}. A task's crypto.randomUUID()
  // satisfies that, so no server change is involved and a task cannot borrow
  // another scope's authorisation by naming a different id.
  //
  // Memoised because LexicalRichEditor remounts its media plugin when the
  // object identity changes, which would tear down an in-flight upload on
  // every render.
  const media = useMemo(
    () => ({
      docId: String(task.id),
      scope: isTeamBoard ? { type: 'team', teamId: String(teamId) } : { type: 'personal' },
    }),
    [task.id, isTeamBoard, teamId]
  );
  // The comment composer uploads under its OWN media id: the attachment
  // service keeps one live rewriter per docId, and sharing the task's id with
  // the description editor left one of them unable to finish an upload.
  const commentMedia = useMemo(() => ({ ...media, docId: `${media.docId}-cmt` }), [media]);

  // The rich composer's "@" menu is open: Escape closes the menu, not the task.
  const mentionOpenRef = useRef(false);
  const setMentionOpen = useCallback((open) => {
    mentionOpenRef.current = open;
  }, []);

  const listRef = useRef(null);
  const titleFocusedRef = useRef(false);
  const prevCommentCountRef = useRef((task.comments || []).length);

  // Presence + the description's live collab on team boards (moved, unchanged).
  const {
    taskViewers,
    descEditorRef,
    initialDescBlocks,
    collabConfig,
    handleTeamDescriptionHtmlChange,
    handleTeamDescriptionBlocksChange,
  } = useTaskDescriptionCollab({ task, teamId, isTeamBoard, isActive, onChange, user, currentUserId });

  useEffect(() => {
    setTitle(task.title || '');
    prevCommentCountRef.current = (task.comments || []).length;
  }, [task.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A remote rename must still show up while the modal is open (task.title is
  // read fresh every render — everything else here already worked this way).
  // Only resync while the title input ISN'T focused: mid-typing, `task.title`
  // is still the pre-edit value from the parent's perspective (this modal's
  // own onChange hasn't round-tripped back through the task prop yet), so
  // resyncing on every render would otherwise clobber each keystroke.
  useEffect(() => {
    if (titleFocusedRef.current) return;
    setTitle(task.title || '');
  }, [task.title]);

  // Auto-scroll the thread to the newest comment when one is added.
  // listRef holds an OverlayScrollbars component — scroll its viewport element.
  useEffect(() => {
    const count = (task.comments || []).length;
    if (count > prevCommentCountRef.current) {
      const viewport = listRef.current?.osInstance?.()?.elements().viewport;
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    }
    prevCommentCountRef.current = count;
  }, [task.comments]);

  // Scroll-lock the body while open + close on Escape (mirrors the shared Modal).
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      // An editor menu ("/" commands, "@" mentions — description or comment)
      // takes Escape for itself; closing the whole task from under it lost the
      // draft (found by the comment-video check, 01.10.2026).
      const editorMenuOpen = mentionOpenRef.current || !!document.querySelector('.lx-slash-menu, .task-detail-mention-menu, .task-label-menu');
      if (e.key === 'Escape' && !confirmDeleteOpen && !editorMenuOpen) {
        e.stopImmediatePropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, confirmDeleteOpen]);

  const commitTitle = () => {
    titleFocusedRef.current = false;
    const trimmed = title.trim();
    if (trimmed && trimmed !== task.title) onChange({ title: trimmed });
    else if (!trimmed) setTitle(task.title || '');
  };

  /** A comment from the rich composer: plain `body` always, `bodyHtml` when it is more than words. */
  const addComment = ({ html, text, picked }) => {
    const comment = buildComment({
      id: newId(),
      body: text,
      bodyHtml: commentNeedsHtml(html) ? html : null,
      authorName: myName,
      authorId: currentUserId,
      picked,
    });
    onChange({ comments: [...(task.comments || []), comment] });
  };

  const deleteComment = (commentId) => {
    onChange({ comments: (task.comments || []).filter((c) => c.id !== commentId) });
  };

  const sortedComments = (task.comments || [])
    .slice()
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const scopeKey = teamId != null ? `team:${teamId}` : personalScopeId();

  /** Only the task's link (owner, 05.10.2026): opening it opens the board with this task (utils/boardLinks); agents read it over MCP too. */
  const copyLink = async () => {
    if (!boardId) return;
    const ok = await copyTextToClipboard(taskLink({ teamId, boardId, taskId: task.id }));
    if (ok) useNotificationStore.getState().success('A link to this task is on the clipboard.', 'Link copied');
    else useNotificationStore.getState().error('The clipboard is not available here.', 'Link not copied');
  };

  const history = useScopeItemHistory({ scopeKey, itemId: task.id, itemName: task.title, kind: 'task' });
  const aiMark = useMemo(() => aiMarkState(task, task.descriptionHtml || ''), [task]);
  const moveItems =
    onMove && Array.isArray(lists)
      ? lists
          .filter((g) => g.id !== task.groupId)
          .map((g) => ({ label: g.name || 'Untitled list', onClick: () => onMove(g.id) }))
      : [];

  // Every action lives in the head's "…" menu (owner, 06.10.2026), in groups:
  // share · move · history · delete (Close is the X beside it). Send is team boards only: a share
  // carries ids and is resolved against the reader's own board, so on a private
  // board there is nobody it could resolve for. Delete is hidden when the host
  // offers none — the chat quick view does not, because a destructive action
  // reached by following a link someone else sent is the wrong place for it.
  const menuItems = [
    boardId && { icon: <Link2 size={14} />, label: 'Copy link', onClick: copyLink },
    // Copy key / Copy branch name (commit ↔ task links).
    ...taskKeyMenuItems(taskKey, task.title, { key: <Hash size={14} />, branch: <GitBranch size={14} /> }),
    isTeamBoard && { icon: <Send size={14} />, label: 'Send to chat', onClick: () => setShareOpen(true) },
    moveItems.length > 0 && { separator: true },
    // One row with the lists in a submenu (owner, 06.10.2026), not a row per list.
    moveItems.length > 0 && { icon: <ArrowRight size={14} />, label: 'Move to', items: moveItems },
    history.available && { separator: true },
    history.available && { icon: <History size={14} />, label: 'History', onClick: history.open },
    onDelete && { separator: true },
    onDelete && { icon: <Trash2 size={14} />, label: 'Delete task', danger: true, onClick: () => setConfirmDeleteOpen(true) },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 modal-backdrop task-detail-backdrop" onClick={onClose} />

      <div className="task-detail-dialog task-modal" role="dialog" aria-label={task.title || 'Task'} onMouseDown={(e) => e.stopPropagation()}>
        <TaskModalHeader
          menuItems={menuItems}
          taskKey={taskKey}
          onClose={onClose}
          createdAt={task.createdAt}
          updatedAt={task.updatedAt}
          closedTask={task}
          timeAgo={timeAgo}
          viewers={isTeamBoard ? taskViewers : null}
          title={title}
          onTitleChange={setTitle}
          onTitleFocus={() => {
            titleFocusedRef.current = true;
          }}
          onTitleCommit={commitTitle}
        />
        <TaskMetaChips
          task={task}
          isTeamBoard={isTeamBoard}
          members={members}
          currentUserId={currentUserId}
          assigneeValue={assigneeValue}
          palette={palette}
          heldLabels={heldLabels}
          onChange={onChange}
          onAddLabel={onAddLabel}
        />

        <OsScroll className="task-detail-main-scroll task-modal-scroll" ref={listRef}>
          <div className="task-modal-body">
            <section className="task-modal-section">
              <span className="task-modal-label">
                Description
                {/* AI Act Art. 50: created or edited by an AI (aiAuthorship.js). */}
                <AiContentChip mark={aiMark} />
              </span>
              <div className="task-detail-description">
                <LexicalRichEditor
                  key={task.id}
                  ref={descEditorRef}
                  fallback={<div className="task-detail-desc-loading">Loading editor…</div>}
                  value={task.descriptionHtml || ''}
                  valueBlocks={isTeamBoard ? initialDescBlocks : null}
                  blockIdSeed={isTeamBoard ? task.id : null}
                  onChange={isTeamBoard ? handleTeamDescriptionHtmlChange : (html) => onChange({ descriptionHtml: html })}
                  onBlocksChange={isTeamBoard ? handleTeamDescriptionBlocksChange : undefined}
                  collab={collabConfig}
                  media={media}
                  placeholder="Add a description, or press '/' for commands…"
                  blockHandles={false}
                />
              </div>
            </section>

            <TaskSubtasks task={task} onChange={onChange} />

            {/* Commits that name this task's key (commit ↔ task links, slice 1). */}
            {taskKey && boardId && <TaskCommits taskKey={taskKey} aliasKeys={aliasKeys} title={task.title} boardId={boardId} task={task} onChange={onChange} />}

            <section className="task-modal-section task-detail-comments">
              <span className="task-modal-label">
                Comments
                <span className="task-detail-comments-count">{sortedComments.length}</span>
              </span>
              <TaskCommentList comments={sortedComments} members={members} labelOf={memberLabel} timeAgo={timeAgo} onDelete={deleteComment} />
            </section>
          </div>
        </OsScroll>

        {/* Composer pinned at the bottom, the whole width — always visible. */}
        <div className="task-modal-foot">
          {/* Your own picture beside the box you are typing in. */}
          <UserAvatar className="task-detail-comment-avatar" userId={currentUserId} name={myName} size={30} />
          <div className="task-modal-composer">
            <CommentComposer
              media={commentMedia}
              members={members}
              labelOf={memberLabel}
              mentions={isTeamBoard}
              onSubmit={addComment}
              onMentionOpenChange={setMentionOpen}
              draftKey={`${boardId || 'board'}:${task.id}`}
            />
          </div>
        </div>
      </div>

      {history.modal}

      <ShareTaskToChatModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        task={task}
        board={{ id: boardId, visibility: 'team', teamId }}
        members={members}
      />

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={onDelete}
        title="Delete task"
        message={`Delete "${task.title || 'this task'}"? This can't be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
}
